import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, Mail, Phone, User, MapPin, MessageSquare, Check, Briefcase, Hotel, Home, ArrowLeft, ArrowRight, Calendar, BarChart3, Wifi, FileText, Bot } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HOTEL_TYPES = ['Hotel', 'Hostal', 'Resort', 'Bed & Breakfast', 'Posada', 'Apart-Hotel', 'Glamping', 'Otro'];
const APARTMENT_TYPES = ['Apartamento Turístico', 'Casa Vacacional', 'Apartaestudio', 'Villa', 'Cabaña', 'Loft', 'Penthouse', 'Otro'];

const SERVICES = [
  {
    id: 'pms',
    name: 'Servicio PMS',
    price: 30000,
    icon: Calendar,
    description: 'Calendario de reservas, habitaciones, inventarios, dashboard, informes de ingresos y egresos',
    required: true,
    color: 'blue'
  },
  {
    id: 'channel_manager',
    name: 'Channel Manager',
    price: 40000,
    icon: Wifi,
    description: 'Conectividad con OTAs (Booking, Airbnb, Expedia). Sincronización automática de disponibilidad y tarifas',
    required: false,
    color: 'purple'
  },
  {
    id: 'facturacion',
    name: 'Facturación Electrónica',
    price: 50000,
    icon: FileText,
    description: 'Generación de facturas electrónicas para tus huéspedes y gestión tributaria',
    required: false,
    color: 'emerald'
  },
  {
    id: 'chatbot',
    name: 'Chatbot WhatsApp',
    price: 40000,
    icon: Bot,
    description: 'Bot de reservas automáticas por WhatsApp. Tus clientes reservan directamente desde su celular',
    required: false,
    color: 'green'
  }
];

function formatCOP(amount) {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
}

export default function QuotePage() {
  const [step, setStep] = useState(1);
  const [pmsType, setPmsType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedServices, setSelectedServices] = useState(['pms']);
  const [formData, setFormData] = useState({
    nombre: '', email: '', telefono: '', empresa: '',
    tipo_propiedad: '', num_propiedades: 1, num_habitaciones_aprox: 10,
    ciudad: '', mensaje: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectPmsType = (type) => {
    setPmsType(type);
    if (type === 'apartamento') {
      setFormData(prev => ({ ...prev, num_propiedades: 1, num_habitaciones_aprox: 1 }));
    } else {
      setFormData(prev => ({ ...prev, num_propiedades: 1, num_habitaciones_aprox: 10 }));
    }
    setStep(2);
  };

  const toggleService = (serviceId) => {
    if (serviceId === 'pms') return; // PMS is required
    setSelectedServices(prev =>
      prev.includes(serviceId) ? prev.filter(s => s !== serviceId) : [...prev, serviceId]
    );
  };

  const totalMensual = SERVICES.filter(s => selectedServices.includes(s.id)).reduce((sum, s) => sum + s.price, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/quotes/request`, {
        ...formData,
        pms_type: pmsType,
        num_propiedades: parseInt(formData.num_propiedades),
        num_habitaciones_aprox: parseInt(formData.num_habitaciones_aprox),
        servicios_seleccionados: selectedServices,
        total_mensual: totalMensual
      });
      setSubmitted(true);
      toast.success('Solicitud enviada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setLoading(false);
    }
  };

  // Success Screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-slate-800/50 border-slate-700">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4" style={{fontFamily: 'Manrope'}}>
              ¡Solicitud Enviada!
            </h2>
            <p className="text-slate-400 mb-2">
              Hemos recibido tu cotización. Nuestro equipo se pondrá en contacto contigo en las próximas 24 horas.
            </p>
            <div className="bg-slate-900/50 rounded-lg p-4 my-4 text-left">
              <p className="text-slate-300 text-sm font-medium mb-2">Servicios seleccionados:</p>
              {SERVICES.filter(s => selectedServices.includes(s.id)).map(s => (
                <div key={s.id} className="flex justify-between text-sm text-slate-400 py-1">
                  <span>{s.name}</span>
                  <span className="text-white">{formatCOP(s.price)}/mes</span>
                </div>
              ))}
              <div className="border-t border-slate-700 mt-2 pt-2 flex justify-between font-bold text-white">
                <span>Total Mensual</span>
                <span>{formatCOP(totalMensual)}</span>
              </div>
            </div>
            <div className="space-y-3">
              <Link to="/">
                <Button className="w-full">Volver al Inicio</Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="w-full border-slate-600 text-slate-300">
                  ¿Ya tienes cuenta? Inicia Sesión
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 1: Select PMS Type
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <img src="https://static.prod-images.emergentagent.com/jobs/66419162-0afe-4996-ab1a-5d519d29062e/images/1419e719ba240b6e00d42093e097a0701e8eac9d9c7b54db9859b4af59d92017.png" alt="Edén" className="h-10 w-10 rounded" />
              <span className="text-2xl font-bold text-white" style={{fontFamily: 'Manrope'}}>Edén</span>
            </Link>
            <h1 className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Manrope'}}>¿Qué deseas cotizar?</h1>
            <p className="text-slate-400">Selecciona el tipo de sistema que mejor se adapte a tu negocio</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card
              className="bg-slate-800/50 border-slate-700 hover:border-primary/50 cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
              onClick={() => handleSelectPmsType('hotel')}
              data-testid="select-hotel-btn"
            >
              <CardContent className="pt-8 pb-6 text-center">
                <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-500/30 transition-colors">
                  <Hotel className="h-10 w-10 text-blue-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3" style={{fontFamily: 'Manrope'}}>Hotel</h2>
                <p className="text-slate-400 mb-6 text-sm">Sistema completo para gestión hotelera</p>
                <Button type="button" className="w-full" variant="outline" onClick={(e) => { e.stopPropagation(); handleSelectPmsType('hotel'); }}>
                  Seleccionar <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
            <Card
              className="bg-slate-800/50 border-slate-700 hover:border-sky-500/50 cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
              onClick={() => handleSelectPmsType('apartamento')}
              data-testid="select-apartamento-btn"
            >
              <CardContent className="pt-8 pb-6 text-center">
                <div className="w-20 h-20 bg-sky-500/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-sky-500/30 transition-colors">
                  <Home className="h-10 w-10 text-sky-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-3" style={{fontFamily: 'Manrope'}}>Apartamentos</h2>
                <p className="text-slate-400 mb-6 text-sm">Gestión de múltiples unidades independientes</p>
                <Button type="button" className="w-full border-sky-500/50 text-sky-400 hover:bg-sky-500/20" variant="outline" onClick={(e) => { e.stopPropagation(); handleSelectPmsType('apartamento'); }}>
                  Seleccionar <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-slate-500 text-sm mt-8">
            ¿Ya tienes cuenta? <Link to="/login" className="text-primary hover:underline">Inicia sesión aquí</Link>
          </p>
        </div>
      </div>
    );
  }

  // Step 2: Form + Service Selection
  const propertyTypes = pmsType === 'hotel' ? HOTEL_TYPES : APARTMENT_TYPES;
  const isApartment = pmsType === 'apartamento';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <img src="https://static.prod-images.emergentagent.com/jobs/66419162-0afe-4996-ab1a-5d519d29062e/images/1419e719ba240b6e00d42093e097a0701e8eac9d9c7b54db9859b4af59d92017.png" alt="Edén" className="h-10 w-10 rounded" />
            <span className="text-2xl font-bold text-white" style={{fontFamily: 'Manrope'}}>Edén</span>
          </Link>
          <div className="flex items-center justify-center gap-3 mb-4">
            <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="text-slate-400 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" /> Cambiar
            </Button>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isApartment ? 'bg-sky-500/20 text-sky-400' : 'bg-blue-500/20 text-blue-400'}`}>
              {isApartment ? <><Home className="h-3 w-3 inline mr-1" /> Apartamentos</> : <><Hotel className="h-3 w-3 inline mr-1" /> Hotel</>}
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Manrope'}}>Cotiza tu Plan</h1>
          <p className="text-slate-400">Selecciona los servicios que necesitas y completa tus datos</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Service Selection - Left */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-lg font-semibold text-white" style={{fontFamily: 'Manrope'}}>Selecciona tus servicios</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SERVICES.map(service => {
                const Icon = service.icon;
                const isSelected = selectedServices.includes(service.id);
                const colorMap = { blue: 'blue', purple: 'purple', emerald: 'emerald', green: 'green' };
                const c = colorMap[service.color] || 'blue';
                return (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => toggleService(service.id)}
                    data-testid={`service-${service.id}`}
                    className={`text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                      isSelected
                        ? `border-${c}-500 bg-${c}-500/10`
                        : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                    } ${service.required ? 'ring-1 ring-blue-500/30' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? `bg-${c}-500/20` : 'bg-slate-700'}`}>
                        <Icon className={`h-5 w-5 ${isSelected ? `text-${c}-400` : 'text-slate-400'}`} />
                      </div>
                      {isSelected && (
                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <Check className="h-3.5 w-3.5 text-white" />
                        </div>
                      )}
                    </div>
                    <h4 className={`font-semibold mb-1 ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                      {service.name}
                      {service.required && <span className="text-[10px] ml-1.5 bg-blue-500/30 text-blue-300 px-1.5 py-0.5 rounded">Incluido</span>}
                    </h4>
                    <p className="text-xs text-slate-400 mb-2 leading-relaxed">{service.description}</p>
                    <p className={`text-lg font-bold ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                      {formatCOP(service.price)}<span className="text-xs font-normal text-slate-400">/mes</span>
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Contact Form */}
            <Card className="bg-slate-800/50 border-slate-700 mt-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg" style={{fontFamily: 'Manrope'}}>Información de Contacto</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-200">Nombre completo *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input placeholder="Tu nombre" value={formData.nombre} onChange={(e) => handleChange('nombre', e.target.value)} required className="pl-10 bg-slate-900 border-slate-600 text-white" data-testid="quote-nombre" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-200">Empresa (opcional)</Label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input placeholder="Nombre de tu empresa" value={formData.empresa} onChange={(e) => handleChange('empresa', e.target.value)} className="pl-10 bg-slate-900 border-slate-600 text-white" data-testid="quote-empresa" />
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-200">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input type="email" placeholder="tu@email.com" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required className="pl-10 bg-slate-900 border-slate-600 text-white" data-testid="quote-email" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-200">Teléfono *</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input placeholder="+57 300 123 4567" value={formData.telefono} onChange={(e) => handleChange('telefono', e.target.value)} required className="pl-10 bg-slate-900 border-slate-600 text-white" data-testid="quote-telefono" />
                      </div>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-700">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-200">Tipo de Propiedad *</Label>
                        <Select value={formData.tipo_propiedad} onValueChange={(v) => handleChange('tipo_propiedad', v)}>
                          <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="quote-tipo">
                            <SelectValue placeholder="Selecciona tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            {propertyTypes.map((type) => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-200">Ciudad *</Label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input placeholder="Cartagena, Medellín..." value={formData.ciudad} onChange={(e) => handleChange('ciudad', e.target.value)} required className="pl-10 bg-slate-900 border-slate-600 text-white" data-testid="quote-ciudad" />
                        </div>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-slate-200">{isApartment ? 'Número de Apartamentos *' : 'Número de Propiedades *'}</Label>
                        <Input type="number" min="1" max={isApartment ? 10 : 100} value={formData.num_propiedades} onChange={(e) => handleChange('num_propiedades', e.target.value)} required className="bg-slate-900 border-slate-600 text-white" data-testid="quote-num-props" />
                      </div>
                      {!isApartment && (
                        <div className="space-y-2">
                          <Label className="text-slate-200">Habitaciones Aproximadas *</Label>
                          <Input type="number" min="1" value={formData.num_habitaciones_aprox} onChange={(e) => handleChange('num_habitaciones_aprox', e.target.value)} required className="bg-slate-900 border-slate-600 text-white" data-testid="quote-num-rooms" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-200 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" /> Mensaje adicional (opcional)
                    </Label>
                    <Textarea placeholder="Cuéntanos sobre tu negocio o preguntas específicas..." value={formData.mensaje} onChange={(e) => handleChange('mensaje', e.target.value)} rows={3} className="bg-slate-900 border-slate-600 text-white" data-testid="quote-mensaje" />
                  </div>

                  <Button type="submit" size="lg" className="w-full bg-primary hover:bg-primary/90" disabled={loading} data-testid="quote-submit-btn">
                    {loading ? 'Enviando...' : 'Solicitar Cotización'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Pricing Summary - Right sidebar */}
          <div className="lg:sticky lg:top-8 h-fit space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg" style={{fontFamily: 'Manrope'}}>Resumen de tu Plan</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {SERVICES.filter(s => selectedServices.includes(s.id)).map(s => (
                  <div key={s.id} className="flex justify-between items-center py-2 border-b border-slate-700/50 last:border-0">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-slate-300">{s.name}</span>
                    </div>
                    <span className="text-sm text-white font-medium">{formatCOP(s.price)}</span>
                  </div>
                ))}
                <div className="pt-3 border-t-2 border-slate-600">
                  <div className="flex justify-between items-baseline">
                    <span className="text-slate-300 font-medium">Total Mensual</span>
                    <span className="text-2xl font-bold text-white" data-testid="quote-total">{formatCOP(totalMensual)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">COP / mes (IVA no incluido)</p>
                </div>
              </CardContent>
            </Card>

            <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-4">
              <p className="text-xs text-slate-400 leading-relaxed">
                Todos los precios son mensuales en pesos colombianos. El servicio PMS es obligatorio. Los servicios adicionales se pueden activar o desactivar en cualquier momento.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
